// Every Forms tooltip in one place (Implementation List §3), so copy can be
// edited without touching components.
//
// Entries key on stable attributes, never on styled-components classes:
//   tour: 'x'  matches [data-tour="x"]; those ids are product-tour targets too
//   help: 'x'  matches [data-help="x"]; tooltip-only
// Copy:
//   t  a title makes it a help tip (bold title + explanation). The Help tips
//      switch hides these.
//   b  the text. Without a title it is a short label that always shows.
//   k  keyboard keys shown as badges, space-separated ("Esc", "←").
//   side  preferred side: top (default), bottom, left or right.
// {app} becomes the platform name (Pit Pay, Ticket Hoss). Keep the wording
// neutral: the forms are for anyone, not only racers.
//
// Elements can also carry their own copy (data-tip, data-tip-title,
// data-tip-kbd, data-tip-side), which wins over this list; a plain title=""
// becomes a label. data-tip-pin makes a tip show even with Help tips off and
// lets a tap pin it on touch screens; data-tip-off silences an element and
// everything inside it.

export const REGISTRY = [
  // forms list
  {
    tour: 'forms-new',
    t: 'New form',
    b: 'Name it and pick the track or series it belongs to.',
  },
  {
    tour: 'forms-filter',
    t: 'Filter by account',
    b: 'Show forms for one track or series, or every form across your accounts.',
  },
  {
    tour: 'form-card-status',
    t: 'Live or Draft',
    b: 'Live forms take entries in the {app} app and on the web. Draft forms stay hidden while you build.',
  },
  {
    tour: 'form-card-notes',
    t: 'Team notes',
    b: 'Leave notes on what still needs fixing. Only your team sees them.',
  },
  {
    tour: 'form-card-submissions',
    t: 'Submissions',
    b: 'Every entry for this form, with saved views and exports.',
  },
  { help: 'new-form-card', b: 'Start a new form' },

  // builder
  { help: 'builder-tab-build', b: 'Fields, layout and form settings' },
  { help: 'builder-tab-submissions', b: 'Entries people have sent in' },
  {
    tour: 'builder-status',
    t: 'Draft or Live',
    b: 'Go Live to publish the form in the {app} app and on the web. Only Live forms take entries.',
  },
  {
    tour: 'builder-preview',
    t: 'Preview',
    b: 'See the form the way people filling it in see it, in the app or as a web form.',
  },
  { tour: 'palette-search', b: 'Filter field types by name' },
  {
    help: 'palette-saved-field',
    t: 'Saved field',
    b: 'A field your team saved for reuse. Click to add it to the form.',
  },
  {
    help: 'palette-item',
    t: 'Add a field',
    b: 'Drag it onto the form, or click to add it after the selected field.',
  },
  { help: 'drag-handle', b: 'Drag to reorder' },
  {
    help: 'prefill-tag',
    t: '{app} field',
    b: 'Starts filled in from the person’s {app} profile. They can still change it.',
  },
  {
    tour: 'field-tags',
    t: 'Field tags',
    b: 'The field type, whether it’s required, and whether it pre-fills from a profile.',
  },
  {
    help: 'save-resume',
    t: 'Save and resume',
    b: 'Keeps answers on the person’s phone or browser as they type, so they can leave and finish later on the same device.',
  },

  // submissions
  {
    help: 'copy-link',
    t: 'Copy link',
    b: 'Share the public form in texts, social posts or on your website.',
  },
  { help: 'open-form', b: 'Opens the public web form in a new tab' },
  {
    tour: 'submissions-search',
    t: 'Search entries',
    b: 'Matches any answer, who sent it and the entry ID.',
  },
  {
    tour: 'submissions-export',
    t: 'Export',
    b: 'CSV, Excel or a PDF report. You pick the fields and their order.',
  },
  {
    help: 'view-tab',
    t: 'Saved view',
    b: 'Each view keeps its own set of columns. Switch views any time.',
  },
  {
    help: 'new-view',
    t: 'New view',
    b: 'Save a set of columns as a tab, like a contact list or a check-in sheet.',
  },
  { help: 'edited-mark', b: 'Edited by your team' },
  {
    tour: 'columns-button',
    t: 'Columns',
    b: 'Choose the fields in this view and drag them into order.',
  },

  // entry card and dialogs
  {
    tour: 'entry-edit',
    t: 'Edit entry',
    b: 'Fix a typo or update details. You can also click any answer to edit it.',
  },
  {
    tour: 'entry-download',
    b: 'Save just this entry as a PDF or an Excel sheet',
  },
  { help: 'entry-prev', b: 'Previous entry', k: '←' },
  { help: 'entry-next', b: 'Next entry', k: '→' },
  { help: 'entry-close', b: 'Close', k: 'Esc' },
  {
    help: 'export-preset',
    t: 'Start from a view',
    b: 'Loads that view’s columns. Change anything after.',
  },
  { help: 'dialog-close', b: 'Close', k: 'Esc' },
  { tour: 'help-button', b: 'Help & tours' },
];

export const selectorFor = (entry) =>
  entry.tour ? `[data-tour="${entry.tour}"]` : `[data-help="${entry.help}"]`;

const fill = (text, appName) =>
  text == null ? text : String(text).split('{app}').join(appName);

// The registry with selectors built and {app} filled in.
export const resolveRegistry = (appName, entries = REGISTRY) =>
  entries.map((entry) => ({
    sel: selectorFor(entry),
    t: fill(entry.t, appName),
    b: fill(entry.b, appName),
    k: entry.k || null,
    side: entry.side || null,
  }));
